import { PaymentMethodPicker, type PaymentMethod } from '../components/PaymentMethod';
/**
 * Abunə ekranı — poçtun açılmasının yeganə yeri.
 *
 * NİYƏ BELƏ QURULUB
 *
 * ⚠️ HESAB DOMEN SAYINDAN ÇIXIR, QUTU SAYINDAN YOX (2026-08-08 tarifi).
 * Bir domendə neçə qutu açıldığının məbləğə təsiri yoxdur. Ekranda hər
 * iki rəqəm göstərilir, amma vurulan yalnız domendir — müştəri «12 qutu,
 * 5 AZN» sətrini görəndə təklifin nə olduğunu bir baxışda anlayır.
 *
 * ⚠️ SAY SORUŞULMUR, OXUNUR. Rəqiblərin çoxu qeydiyyatda «neçə
 * istifadəçi?» soruşur — halbuki müştəri hələ məhsulu görməyib və
 * bilmir. Burada rəqəm `domains` cədvəlindən gəlir: müştəri qərarı
 * artıq domeni bağlayarkən verib.
 *
 * ⚠️ HÜQUQİ KİMLİK YALNIZ BURADA SORUŞULUR, qeydiyyatda yox. VÖEN və
 * hüquqi ad ziyarətçilərin 100%-indən istənsə, funnel-in başında
 * itirilən adam sayı, sonda qazanılandan çoxdur.
 *
 * ⚠️ İKİ ÖDƏNİŞ YOLU VAR VƏ HƏR İKİSİ QALIR (2026-08-17).
 *
 *   1) KARTLA — əsas hərəkət. Öz-özünə işləyir: müştəri gecə saat 2-də
 *      ödəyir, abunə həmin dəqiqə açılır, operator heç nə etmir.
 *   2) FAKTURA İLƏ — ikinci dərəcəli, amma SİLİNMİR. Azərbaycanda
 *      dövlət qurumu, orta və iri şirkət xərci mühasibatlıqdan keçirmək
 *      üçün bank köçürməsi ilə ödəyir: korporativ kart ya yoxdur, ya da
 *      onunla onlayn ödəniş qadağandır. Faktura yolunu «artıq lazım
 *      deyil» deyib götürsək, məhz ən böyük müştərilərin qarşısını
 *      kəsərik.
 *
 * ⚠️ KART SAHƏSİ BU EKRANDA YOXDUR VƏ HEÇ VAXT OLMAMALIDIR. Nömrə
 * şlüzün öz səhifəsində yığılır; bizim domenimizdə kart məlumatına
 * toxunan tək sətir belə olsa, PCI DSS öhdəliyi tamam başqa səviyyəyə
 * qalxır. Aşağıdakı «Kart məlumatlarınız bizim serverə düşmür» cümləsi
 * marketinq deyil — həm faktdır, həm də bu qaydanın yazılı izi.
 */

import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { fullDate } from '../lib/format';
import type { BillingSummary, Payment } from '../types';

/** Qəpiyi AZN kimi göstərir: 200 → «2.00 AZN». */
function money(minor: number, currency: string): string {
  return `${(minor / 100).toFixed(2)} ${currency}`;
}

/*
  MÜDDƏT SEÇİMİ.

  ⚠️ 12 AY ÜÇÜN ENDİRİM YOXDUR — QİYMƏT XƏTTİ ARTIR (aylıq məbləğ × ay).
  Bu, unudulmuş iş deyil, qərardır: endirimi kodda «məntiqli görünür»
  deyə vermək qiymət siyasətini proqramçının əlinə keçirir. Bir dəfə
  verilən endirimi geri götürmək isə müştəri gözündə QİYMƏT ARTIMIDIR.
  Endirim veriləcəksə, tarif cədvəlində (panel-api/src/billing/lifecycle.ts
  → PRICES) rəsmiləşdirilməli və serverdə hesablanmalıdır — burada yox,
  çünki məbləği onsuz da server hesablayır (bax api.startPayment).

  ⚠️ 12-dən yuxarı dəyər əlavə etmək olmaz: server ucu 1..12 aralığını
  qəbul edir (server.ts → /api/billing/pay), bazada isə CHECK 1..24-dür.
  Buraya 24 yazsaq, düymə görünər, amma sorğu 400 ilə qayıdar.
*/
const MONTH_OPTIONS = [1, 3, 6, 12] as const;

function statusLabel(s: string): { text: string; tone: string } {
  switch (s) {
    case 'unpaid':
      // ⚠️ `is-warn`, `is-info` DEYİL. Bu vəziyyətdə poçt ÜMUMİYYƏTLƏ
      // işləmir — sakit mavi rəng müştəriyə «hər şey qaydasındadır»
      // deyir və o, məktubunu boş yerə gözləyir.
      return { text: 'Ödəniş gözlənilir', tone: 'is-warn' };
    case 'trial':
      return { text: 'Sınaq müddəti', tone: 'is-info' };
    case 'active':
      return { text: 'Aktiv abunə', tone: 'is-ok' };
    case 'past_due':
      return { text: 'Ödəniş gözlənilir', tone: 'is-warn' };
    case 'suspended':
      return { text: 'Dayandırılıb', tone: 'is-warn' };
    default:
      return { text: s, tone: '' };
  }
}

/**
 * Ödəniş sətrinin vəziyyəti — Azərbaycanca.
 *
 * ⚠️ XAM KODU («declined») EKRANA ÇIXARMAQ OLMAZ. Müştəri onu oxuya
 * bilmir və dəstəyə «declined nədir?» sualı ilə yazır. Naməlum dəyər
 * gəlsə (serverdə yeni status yaransa) kod olduğu kimi göstərilir —
 * boş xana göstərməkdən yaxşıdır, çünki heç olmasa dəstək görür.
 */
function paymentLabel(s: string): { text: string; tone: string } {
  switch (s) {
    case 'approved':
      return { text: 'Ödənilib', tone: 'is-ok' };
    case 'pending':
      // ⚠️ «Uğursuz» DEYİL. `pending` = şlüzdən hələ dəqiq cavab yoxdur.
      // Uzlaşdırma 10 dəqiqədən bir yenidən soruşur və sətir çox vaxt
      // öz-özünə «Ödənilib»ə keçir (bax payments.ts → reconcilePending).
      return { text: 'Yoxlanılır', tone: 'is-wait' };
    case 'declined':
      return { text: 'Bank imtina etdi', tone: 'is-bad' };
    case 'failed':
      return { text: 'Alınmadı', tone: 'is-bad' };
    case 'refunded':
      return { text: 'Geri qaytarılıb', tone: 'is-wait' };
    default:
      return { text: s, tone: '' };
  }
}

export function Billing(): React.ReactElement {
  const [data, setData] = useState<BillingSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  // Kartla ödəniş.
  const [months, setMonths] = useState<number>(1);
  /** Şlüzə yönləndirmə gedir — düymə bloklanır (bax `startPay`). */
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [history, setHistory] = useState<Payment[]>([]);


  useEffect(() => {
    void api
      .billing()
      .then((r) => setData(r.billing))
      .catch(() => setError('Abunə məlumatı yüklənmədi.'));
  }, []);

  /*
    ⚠️ TARİXÇƏNİN XƏTASI EKRANA ÇIXARILMIR — QƏSDƏN.
    Bu, ikinci dərəcəli məlumatdır. Uc 404/500 versə (məsələn `payments`
    cədvəli hələ miqrasiya olunmayıbsa), abunə ekranının qalan hissəsi
    işləməyə davam etməlidir: ödəniş düyməsini xəta mesajı ilə əvəz etmək
    müştərini heç nə edə bilmədiyi ekranda qoyur.
  */
  useEffect(() => {
    void api
      .payments()
      .then((r) => setHistory(r.payments))
      .catch(() => setHistory([]));
  }, []);

  if (error && !data) {
    return (
      <div className="billing">
        <p className="login-error">{error}</p>
      </div>
    );
  }
  if (!data) return <div className="spinner" aria-label="Yüklənir" />;

  const st = statusLabel(data.status);
  /** Seçilmiş müddətin yekunu. Xətti vurma — endirim yoxdur (yuxarıya bax). */
  const payTotal = data.totalMinor * months;

  /**
   * Ödənişi başladır və istifadəçini şlüzün səhifəsinə atır.
   *
   * ⚠️ ABUNƏNİ BU DÜYMƏ AÇMIR. Server yalnız `pending` sətir yaradır və
   * URL qaytarır; abunəni şlüzün öz təsdiqi açır. Ona görə burada heç bir
   * «uğurlu» vəziyyəti göstərilmir — nəticə qayıdış ünvanından gəlir
   * (App.tsx → ?bolme=abune&odenis=…).
   */
  async function startPay(): Promise<void> {
    // ⚠️ İKİQAT KLİK QORUMASI KODDA DA VAR, təkcə `disabled`-də yox.
    // Şlüz səhifəsi yavaş açılanda müştəri düyməni arxa-arxaya basır və
    // hər basış bazada AYRI `pending` sətir yaradır; sonra uzlaşdırma
    // onların hər birini ayrıca soruşur. Serverdə də limit var, amma ilk
    // müdafiə xətti burada olmalıdır.
    if (paying) return;
    setPaying(true);
    setPayError(null);
    try {
      const { url } = await api.startPayment(months, paymentMethod);
      // ⚠️ `assign`, `replace` DEYİL. `replace` paneli tarixçədən silir və
      // şlüzdə «Geri» basan müştəri bizim səhifəyə qayıda bilmir — ödəniş
      // yarımçıq qalır və o, panelini itirdiyini düşünür.
      window.location.assign(url);
      // ⚠️ `setPaying(false)` BURADA YOXDUR — QƏSDƏN. Yönləndirmə
      // başlayıb; vəziyyəti geri qaytarsaq, səhifə ölməzdən əvvəl düymə
      // bir anlıq yenidən aktivləşir və o anda basmaq ikinci sətir yaradır.
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      setPayError(
        code === 'too_many_attempts'
          ? 'Ard-arda çox cəhd oldu. Bir neçə dəqiqədən sonra yenidən yoxlayın.'
          : code === 'not_configured'
            ? 'Kartla ödəniş hazırda əlçatan deyil. Faktura ilə ödəyə bilər ' +
              'və ya bir azdan yenidən cəhd edə bilərsiniz.'
            : code === 'invalid_months' || code === 'bad_months'
              ? 'Seçilən müddət qəbul edilmədi. 1, 3, 6 və ya 12 ay seçin.'
              : code === 'no_amount'
                ? 'Məbləğ hesablanmadı. Bizə yazın — hesabınıza baxaq.'
                : 'Ödəniş səhifəsi açılmadı. Kartınızdan heç nə çıxılmayıb — ' +
                  'bir azdan yenidən cəhd edin.',
      );
      setPaying(false);
    }
  }

  /**
   * Bank köçürməsi sorğusu — REKVİZİTSİZ.
   *
   * ⚠️ ƏVVƏL BU FUNKSİYA ALTI XANALIQ FORMA GÖNDƏRİRDİ (hüquqi ad, VÖEN,
   * ünvan, şəhər, e-poçt, telefon) və faktura yalnız onlar doldurulandan
   * sonra istənilə bilirdi. Sahibin qərarı ilə forma silindi: ödəməyə
   * hazır müştərinin qarşısına sənəd qoymaq onu itirməyin ən sürətli
   * yoludur. Operator rekvizitləri lazım olsa e-poçtla soruşur — və
   * çox vaxt lazım olmur, çünki müştərilərin əksəri kartla ödəyir.
   */
  async function askInvoice(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.requestInvoice();
      setSent(true);
      const r = await api.billing();
      setData(r.billing);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      setError(
        code === 'too_many_attempts'
          ? 'Sorğunuz artıq qeydə alınıb — tezliklə əlaqə saxlayacağıq.'
          : 'Sorğu göndərilmədi. Bir azdan yenidən yoxlayın.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="billing">
      <header className="billing-head">
        <h1>Abunə</h1>
        <span className={`billing-status ${st.tone}`}>{st.text}</span>
      </header>

      {/*
        ÖDƏNİŞ HƏLƏ EDİLMƏYİB — POÇT BAĞLIDIR.
        ⚠️ BU ZOLAQ NƏYİN İŞLƏMƏDİYİNİ DƏQİQ SAYIR. «Abunə aktiv deyil»
        yazsaq, müştəri panelin işlədiyini görüb poçtun da işlədiyini
        güman edər, göndərənə ünvanını verər və məktublar səssizcə geri
        dönər. Ən bahalı nasazlıq növü budur: müştəri onu bizdən yox,
        itirilmiş məktubdan öyrənir.
      */}
      {data.status === 'unpaid' ? (
        <div className="banner is-warn" role="status">
          <span>
            <strong>Poçt hələ işləmir.</strong> Ödəniş qeydə alınana qədər bu
            domenə məktub nə gəlir, nə də göndərilir — göndərənlər «çatdırılmadı»
            cavabı alır. Panel, domen quraşdırması və qutu yaratmaq açıqdır;
            ödənişdən dərhal sonra poçt işə düşür.
          </span>
        </div>
      ) : null}

      {/*
        ABUNƏ SAYĞACI.
        ⚠️ SON GÜNLƏRDƏ DEYİL, ƏVVƏLCƏDƏN GÖRÜNÜR. «3 gün qaldı» ilk dəfə
        son günlərdə görünsə, müştəri hazırlaşmağa vaxt tapmır və qərarı
        təcili şəraitdə verir — belə qərar çox vaxt «getmək» olur.
      */}
      {data.daysLeft !== null ? (
        <div className={`banner ${data.daysLeft <= 3 ? 'is-warn' : 'is-info'}`} role="status">
          <span>
            Abunənin bitməsinə <strong>{data.daysLeft} gün</strong> qalıb.
            Yenilənməsə, gələn məktublar gəlməyə davam edir; yalnız göndəriş
            dayanır.
          </span>
        </div>
      ) : null}

      {data.status === 'past_due' ? (
        <div className="banner is-warn" role="status">
          <span>
            <strong>Göndəriş dayandırılıb.</strong> Gələn məktublar hələ də qəbul
            olunur. Abunə rəsmiləşdirilməsə, bir həftədən sonra poçt qəbulu da
            dayanacaq.
          </span>
        </div>
      ) : null}

      {/* ── Hesablama ─────────────────────────────────────────────────── */}
      <section className="billing-card">
        <h2>Aylıq məbləğ</h2>
        <table className="billing-calc">
          <tbody>
            <tr>
              <th>Domen</th>
              {/*
                ⚠️ SAY OXUNUR, SORUŞULMUR — müştəri onu domeni bağlayarkən
                onsuz da müəyyən edib. `|| 1` — hələ domen bağlamayana da
                qiymət göstərilir; «0 domen, 0 AZN» qiyməti gizlətməkdir.
              */}
              <td>{data.domainCount || 1}</td>
            </tr>
            <tr>
              <th>Domen başına / ay</th>
              <td>{money(data.unitPriceMinor, data.currency)}</td>
            </tr>
            {/*
              ⚠️ QUTU SƏTRİ HESABLAMANIN İÇİNDƏDİR, KƏNARINDA DEYİL — VƏ
              MƏBLƏĞ SÜTUNUNDA RƏQƏM YOX, «LİMİTSİZ» YAZILIR.
              Bu sətir təklifin ən güclü hissəsidir: rəqib xidmətlərin
              hamısında məhz burada «× 12 istifadəçi» vurması gedir.
              Cədvəldən çıxarsaq, müştəri onu ya görməz, ya da gec görər.
            */}
            <tr className="billing-free">
              <th>
                Poçt qutusu <small>({data.mailboxCount} ədəd)</small>
              </th>
              <td>Limitsiz — əlavə ödənişsiz</td>
            </tr>
            <tr className="billing-total">
              <th>Cəmi / ay</th>
              <td>{money(data.totalMinor, data.currency)}</td>
            </tr>
          </tbody>
        </table>
        {/*
          İKİNCİ DOMENİN QİYMƏTİ ÖNCƏDƏN DEYİLİR.
          ⚠️ SONRADAN ÖYRƏNİLƏN QİYMƏT ARTIMI ETİBARI POZUR. Müştəri
          ikinci domenini əlavə edib fakturada iki qat məbləğ görsə, bunu
          xəta və ya kələk sayır. Bir cümlə ilə qabaqcadan demək bu
          söhbətin hamısını aradan qaldırır.
        */}
        <p className="billing-note">
          Hər əlavə domen aylıq hesaba {money(data.unitPriceMinor, data.currency)} əlavə
          edir. Bir domendəki qutuların sayı isə heç vaxt qiymətə təsir etmir.
        </p>
        {/*
          ⚠️ QİYMƏT YEKUNDUR — ƏDV YOXDUR.
          Şirkət ƏDV ödəyicisi deyil (sadələşdirilmiş rejim), ona görə
          fakturada ƏDV sətri olmur və göstərilən məbləğ ödəniləcək
          məbləğdir. Bunu AÇIQ yazmaq lazımdır: Azərbaycanda B2B qiymət
          görən adam «üstünə 18% gələcək» deyə güman edir və büdcəsini
          ona görə qurur. Sükut sürprizə çevrilir.

          ⚠️ Şirkət gələcəkdə ƏDV ödəyicisi olarsa (200 000 AZN həddi),
          BU MƏTN VƏ HESABLAMA BİRLİKDƏ DƏYİŞMƏLİDİR — yalnız mətni
          dəyişmək qiyməti yanlış göstərməkdir.
        */}
        <p className="billing-note">
          ƏDV yoxdur — göstərilən məbləğ yekundur, əlavə ödəniş çıxmır.
        </p>
        {data.paidUntil ? (
          <p className="billing-note">
            Ödənilib: <strong>{data.paidUntil.slice(0, 10)}</strong> tarixinə qədər.
          </p>
        ) : null}

        {/* ── Kartla ödəniş — ƏSAS HƏRƏKƏT ─────────────────────────────
          ⚠️ HESABLAMANIN ALTINDA, ayrı kartda deyil. Müştəri rəqəmi görüb
          qərar verir; düymə həmin baxışın içində olmasa, o, «sonra»
          deyib ekrandan çıxır və bir daha qayıtmır.
        */}
        {/*
          ⚠️ ŞLÜZ SÖNÜLÜDÜRSƏ BÜTÜN BLOK GİZLƏNİR, düymə deaktiv edilmir.
          Deaktiv düymə «niyə işləmir?» sualı yaradır və dəstəyə çağırışa
          çevrilir; olmayan düymə isə sual doğurmur — faktura yolu onsuz
          da aşağıdadır. `cardEnabled` sahəsi köhnə cavabda ola bilməz
          (yerləşdirmə anında panel yenidir, API köhnə), ona görə
          `!== false` yazılır: naməlum halda düymə görünür və ən pis
          nəticə bir dəfəlik xəta mesajıdır.
        */}
        {data.cardEnabled !== false ? (
        <div className="pay-box">
          {/*
            MÜDDƏT SEÇİMİ — SADƏ DÜYMƏLƏR, açılan siyahı deyil.
            Dörd variant var; açılan siyahı onları gizlədir və bir klik
            əlavə edir. Vergi/endirim yoxdur, ona görə izah da lazım deyil:
            rəqəm sadəcə vurulur.
          */}
          <div className="pay-months" role="group" aria-label="Ödəniş müddəti">
            {MONTH_OPTIONS.map((m) => (
              <button
                key={m}
                type="button"
                className={`pay-month ${m === months ? 'is-on' : ''}`}
                aria-pressed={m === months}
                disabled={paying}
                onClick={() => setMonths(m)}
              >
                {m} ay
              </button>
            ))}
          </div>

          <PaymentMethodPicker value={paymentMethod} onChange={setPaymentMethod} disabled={paying} />

          <button
            type="button"
            className="btn-primary pay-cta"
            disabled={paying}
            onClick={() => void startPay()}
          >
            {paying
              ? 'Yönləndirilir…'
              : `${paymentMethod === 'card' ? 'Kartla ödə' : 'Apple Pay / Google Pay ilə ödə'} — ${money(payTotal, data.currency)}`}
          </button>

          {months > 1 ? (
            <p className="billing-note">
              {months} ay × {money(data.totalMinor, data.currency)} ={' '}
              <strong>{money(payTotal, data.currency)}</strong>. Uzun müddətə
              endirim tətbiq olunmur — aylıq qiymət dəyişmir.
            </p>
          ) : null}

          {payError ? (
            <p className="login-error" role="alert">
              {payError}
            </p>
          ) : null}

          {/*
            ⚠️ BU İKİ CÜMLƏ EKRANIN ƏN VACİB MƏTNİDİR.
            Azərbaycanda onlayn kart verməkdən çəkinmə real və yayğındır:
            müştəri «kart nömrəm bu şirkətin serverində qalacaq» deyə
            düşünüb düyməni basmır. Şlüzün adını və məlumatın bizə
            düşmədiyini AÇIQ yazmaq bu tərəddüdü aradan qaldırır — və
            eyni zamanda fakt olduğu üçün heç nə vəd etmirik.
          */}
          <p className="billing-note pay-safe">
            Ödəniş Yığım ödəniş sistemi üzərindən aparılır. Kart
            məlumatlarınız bizim serverə düşmür. Ödənişdən dərhal sonra
            abunə uzadılır və poçt işə düşür.
          </p>
        </div>
        ) : (
          /*
            Şlüz bağlıdırsa müştəri boşluq görməməlidir — nə edəcəyini
            bilməlidir. Faktura düyməsi onsuz da aşağıdadır.
          */
          <p className="billing-note">
            Kartla ödəniş hazırda əlçatan deyil. Aşağıdan faktura tələb edib
            bank köçürməsi ilə ödəyə bilərsiniz.
          </p>
        )}
      </section>

      {error ? (
        <p className="login-error" role="alert">
          {error}
        </p>
      ) : null}

      {sent ? (
        <div className="banner is-ok" role="status">
          <span>
            Faktura sorğunuz qeydə alındı. Rekvizitlərinizə uyğun fakturanı
            e-poçtla göndərəcəyik — adətən bir iş günü ərzində.
          </span>
        </div>
      ) : null}

      {/* ── Ödəniş tarixçəsi ──────────────────────────────────────────
        ⚠️ BOŞ HALDA ÜMUMİYYƏTLƏ GÖSTƏRİLMİR. «Hələ ödəniş yoxdur» yazan
        boş cədvəl yeni müştəriyə heç nə vermir, ekranı isə uzadır və
        ödəniş düyməsini aşağı itələyir.

        ⚠️ REFERANS GÖRÜNÜR — dəstək söhbətinin açarı budur. «Pul getdi,
        abunə açılmadı» deyən müştəridən ilk istənilən dəyər referans və
        RRN-dir; onlar ekranda olmasa, söhbət birbaşa banka gedir.
      */}
      {history.length > 0 ? (
        <section className="billing-card">
          <h2>Ödəniş tarixçəsi</h2>
          <table className="pay-table">
            <thead>
              <tr>
                <th>Tarix</th>
                <th>Müddət</th>
                <th>Kart</th>
                <th className="pay-num">Məbləğ</th>
                <th>Vəziyyət</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p) => {
                const ps = paymentLabel(p.status);
                return (
                  <tr key={p.reference}>
                    <td>
                      {fullDate(p.createdAt)}
                      <small className="pay-ref">{p.reference}</small>
                    </td>
                    <td>{p.months} ay</td>
                    <td>
                      {p.panMasked ?? '—'}
                      {p.rrn ? <small className="pay-ref">RRN {p.rrn}</small> : null}
                    </td>
                    <td className="pay-num">{money(p.amountMinor, p.currency)}</td>
                    <td>
                      <span className={`pay-state ${ps.tone}`}>{ps.text}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="billing-note">
            «Yoxlanılır» yazısı normaldır: bank cavabı bəzən gec gəlir və
            vəziyyət 10 dəqiqə ərzində öz-özünə yenilənir. Bir saatdan sonra
            da dəyişməsə, referans nömrəsi ilə bizə yazın.
          </p>
        </section>
      ) : null}

      {/* ── Bank köçürməsi — İKİNCİ DƏRƏCƏLİ ──────────────────────────
        ⚠️ REKVİZİT FORMASI SİLİNDİ (2026-08-18, sahibin qərarı).
        Əvvəl faktura istəmək üçün hüquqi ad, VÖEN, hüquqi ünvan, şəhər,
        e-poçt və telefon doldurulmalı idi — altı xana. Ödəməyə hazır
        müştərinin qarşısına forma qoymaq ən bahalı səhvdir: adam pulu
        verməyə gəlib, sənəd doldurmağa yox. İndi əsas yol kartdır və
        heç nə doldurulmur; bank köçürməsi isə bir kliklə istənilir,
        rekvizitləri lazım olsa operator özü soruşur.

        ⚠️ `btn-secondary` — səhifədə yalnız BİR əsas hərəkət olmalıdır.
        İki eyni görünüşlü düymə qoysaq, kartla ödəyəcək müştəri də
        faktura istəyər: faktura «rəsmi» səslənir. Nəticədə avtomatik
        işləyən yol operator əməyi tələb edən yola çevrilir.
      */}
      {!sent ? (
        <div className="billing-actions">
          <button className="btn-secondary" disabled={busy} onClick={() => void askInvoice()}>
            {busy ? 'Göndərilir…' : 'Bank köçürməsi ilə ödəmək istəyirəm'}
          </button>
        </div>
      ) : null}

      {!sent ? (
        <p className="billing-note">
          Şirkətinizin mühasibatlığı bank köçürməsi tələb edirsə, bu düyməni
          basın — hesab-fakturanı e-poçtunuza göndərəcəyik. Kartla ödənişdə
          isə heç nə doldurmaq lazım deyil, abunə dərhal uzadılır.
        </p>
      ) : null}
    </div>
  );
}
